import { Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { EventCard } from 'components/models/events/EventCard'
import { InventoryCard } from 'components/models/inventories/InventoryCard'
import { TopContentSecondary } from 'components/pages/owners/TopContentSecondary'
import { Section } from 'components/pages/root/Section'
import { FlexScrollWrapper } from 'components/shared/FlexScrollWrapper'
import { Loading } from 'components/shared/Loading'
import { urls } from 'helpers/urls'
import { useInventories } from 'hooks/inventories'
import { useOwnerEventsOngoing, useOwnerEventsUpcoming } from 'hooks/owners'
import { useOwnerEvents } from 'hooks/owners/useOwnerEvents'
import { isEmpty } from 'lodash'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import React, { FC } from 'react'

type Props = {
  owner: Owner
}

export const TopTab: FC<Props> = ({ owner }) => {
  const { xs } = useBreakpoint()
  const { t } = useTranslation()
  const { data: events, isValidating: isValidatingOwnerEvents } =
    useOwnerEvents({ ownerId: owner.id, limit: 10 })
  const { inventories, isValidating: isValidatingOwnerInventories } =
    useInventories({ ownerId: owner.id, limit: 10 })

  const { data: eventsOngoing = [], isValidating: isValidatingEventsOngoing } =
    useOwnerEventsOngoing({ ownerId: owner.id, limit: 10 })
  const {
    data: eventsUpcoming = [],
    isValidating: isValidatingEventsUpcoming,
  } = useOwnerEventsUpcoming({ ownerId: owner.id, limit: 10 })

  const isLoading =
    isValidatingOwnerEvents ||
    isValidatingOwnerInventories ||
    isValidatingEventsOngoing ||
    isValidatingEventsUpcoming
  const isDisplayEmptyStatus =
    isEmpty(events) &&
    isEmpty(inventories) &&
    isEmpty(eventsOngoing) &&
    isEmpty(eventsUpcoming)

  const topContentEvents = [...eventsOngoing, ...eventsUpcoming].slice(0, 3)

  if (isLoading) {
    return (
      <div style={{ paddingTop: xs ? 40 : 60 }}>
        <Loading />
      </div>
    )
  }

  if (isDisplayEmptyStatus) {
    return (
      <div
        style={{
          paddingTop: 160,
          paddingBottom: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          rowGap: 24,
          justifyContent: 'center',
        }}
      >
        <Image src="/no-objects/owner-contents.png" width={151} height={151} />
        <Typography.Text style={{ fontSize: 16, lineHeight: 1 }}>
          Coming Soon...
        </Typography.Text>
      </div>
    )
  }

  return (
    <>
      {topContentEvents.length > 0 && (
        <div style={{ padding: xs ? '40px 16px 0 16px' : '60px 64px 0 64px' }}>
          <TopContentSecondary events={topContentEvents} />
        </div>
      )}
      <div style={{ padding: xs ? '24px 16px' : '40px 64px 0 64px', gap: 64 }}>
        {events && events.length > 0 && (
          <>
            <Section
              title={t('イベント')}
              link={urls.owner(owner.id, 'events')}
            />

            <FlexScrollWrapper alignItems="flex-start">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </FlexScrollWrapper>
          </>
        )}

        {inventories && inventories.length > 0 && (
          <>
            <Section
              title={t('作品')}
              link={urls.owner(owner.id, 'inventories')}
            />

            <FlexScrollWrapper>
              {inventories.map((inventory) => {
                return (
                  <InventoryCard
                    key={inventory.id}
                    inventory={inventory}
                    sideBySide
                  />
                )
              })}
            </FlexScrollWrapper>
          </>
        )}
      </div>
    </>
  )
}
